import React, { useEffect, useState, useRef } from 'react'
import AllStatesPreviousSearchesColumns from './AllStatesPreviousSearchesColumns'
import AllStatesPreviousSearchCard from './AllStatesPreviousSearchCard'
import SimplePagination from 'components/shared/SimplePagination.js/index'
import { getPaginationHeaders } from 'constants/utilityFunctions'
import { getAllStatesSearchHistory, getAllStatesSearchStatus } from 'actions/applicants'
import strings from 'strings'
import { Alert } from '@material-ui/lab'
import Loader from 'components/shared/Loader'
import Drawer from 'components/shared/Drawer'
import { useAppContext } from "../../../../context/AppContext";
import AllStatesSearchDetailsCard from './AllStatesSearchDetailsCard';

const sosStrings = strings.APPLICANT.SOS

const AllStatesPreviousSearches = (props) => {

    const { update, viewResult, search, searchAlternative, searchLoading, applicantId, creditCosts } = props
    const { isDemoMode } = useAppContext()

    const [previousSearches, setPreviousSearches] = useState([])
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [selectedRecord, setSelectedRecord] = useState(null)
    const [searchStatus, setSearchStatus] = useState(null)
    const [isPolling, setIsPolling] = useState(false)
    const intervalRef = useRef(null)

    const previousSearchesData = previousSearches?.response || []
    const previousSearchesPagination = getPaginationHeaders(previousSearches)

    const [paginationParams, setPaginationParams] = useState({})
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        setLoading(true)
        checkSearchStatus()
        getPreviousAllStatesSearches()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [update, paginationParams])

    useEffect(() => {
        // Check status when component mounts
        checkSearchStatus()
        
        // Cleanup interval on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        // Start/stop polling based on search status
        if (searchStatus === 'IN_PROGRESS') {
            startPolling()
        } else {
            stopPolling()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchStatus])

    const checkSearchStatus = async () => {
        if (!applicantId) return
        
        try {
            const statusRes = await getAllStatesSearchStatus(applicantId)()
            setSearchStatus(statusRes.status)
        } catch (err) {
            console.error('Failed to check search status:', err)
        }
    }

    const startPolling = () => {
        if (intervalRef.current) return // Already polling
        
        setIsPolling(true)
        intervalRef.current = setInterval(async () => {
            try {
                const statusRes = await getAllStatesSearchStatus(applicantId)()
                setSearchStatus(statusRes.status)
                
                if (statusRes.status === 'COMPLETED') {
                    // Refresh search history when completed
                    getPreviousAllStatesSearches()
                    stopPolling()
                }
            } catch (err) {
                console.error('Polling error:', err)
            }
        }, 3000) // Poll every 3 seconds
    }

    const stopPolling = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
        setIsPolling(false)
    }

    // get all states searches by applicant id
    const getPreviousAllStatesSearches = () => {
        let params = { applicantId, pagination: paginationParams }
        getAllStatesSearchHistory(params)()
            .then(res => {
                setPreviousSearches(res)
                setLoading(false)
            })
            .catch(() => {
                setLoading(false)
            })
    }

    const handleViewDetails = (record) => {
        setSelectedRecord(record)
        setIsDrawerOpen(true)
    }

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false)
        setSelectedRecord(null)
    }

    return (
        <div className='cardStyle'>
            <div className='uccTitleContainer mt-3'>
                <h5>{sosStrings.SEARCH_HISTORY}</h5>
                {isPolling && (
                    <small className="text-muted ml-2">
                        Search in progress... Refreshing every 3 seconds
                    </small>
                )}
            </div>
            {loading ?
                <Loader />
                :
                (previousSearchesData?.length > 0 ?
                    <>
                        <AllStatesPreviousSearchesColumns />
                        {(previousSearchesData.map((item, index) => (
                            <AllStatesPreviousSearchCard
                                key={item.searchId || index}
                                previousSearch={item}
                                viewResult={viewResult}
                                search={search}
                                searchLoading={searchLoading}
                                applicantId={applicantId}
                                onViewDetails={handleViewDetails}
                            />
                        )))}
                    </>
                    :
                    <Alert severity="warning">{sosStrings.NO_PREVIOUS_SEARCHES}</Alert>
                )
            }
            {previousSearchesPagination?.totalRecords > 0 &&
                <SimplePagination
                    onChangePagination={setPaginationParams}
                    totalRecords={previousSearchesPagination?.totalRecords}
                    totalPages={previousSearchesPagination?.totalPages}
                />
            }

            <Drawer
                open={isDrawerOpen}
                title="Search Details"
                onClose={handleCloseDrawer}
                maxWidth="md"
            >
                {selectedRecord && (
                    <AllStatesSearchDetailsCard
                        searchData={selectedRecord}
                        viewResult={viewResult}
                        searchAlternative={searchAlternative}
                        searchLoading={searchLoading}
                        creditCosts={creditCosts}
                    />
                )}
            </Drawer>
        </div >
    )
}

export default AllStatesPreviousSearches